import { queryOptions, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { MutationConfig, QueryConfig } from '@/app/lib/tanstack-query';
import { createClient, deleteClient, getClient, getClients, updateClient } from '@/app/network/api';
import { getContactsQueryOptions } from '@/contacts/queries';
import { getProjectsQueryOptions } from '@/projects/queries';

type UseCreateClientOptions = {
  mutationConfig?: MutationConfig<typeof createClient>;
};

export const useCreateClient = ({ mutationConfig }: UseCreateClientOptions = {}) => {
  const queryClient = useQueryClient();

  const { onSuccess, ...restConfig } = mutationConfig || {};

  return useMutation({
    onSuccess: (...args) => {
      void queryClient.invalidateQueries({
        queryKey: getClientsQueryOptions().queryKey
      });

      onSuccess?.(...args);
    },
    ...restConfig,
    mutationFn: createClient
  });
};

export const useDeleteClient = ({ mutationConfig }: { mutationConfig?: MutationConfig<typeof deleteClient> } = {}) => {
  const { onSuccess, ...restConfig } = mutationConfig || {};
  const queryClient = useQueryClient();

  return useMutation({
    onSuccess: (data, variables, context) => {
      queryClient.removeQueries({
        queryKey: getClientQueryOptions(variables.clientId).queryKey
      });

      void queryClient.invalidateQueries({ queryKey: getClientsQueryOptions().queryKey });
      void queryClient.invalidateQueries({ queryKey: getProjectsQueryOptions().queryKey });
      onSuccess?.(data, variables, context);
    },
    ...restConfig,
    mutationFn: deleteClient
  });
};

export function getClientQueryOptions(clientId: number) {
  return queryOptions({
    queryKey: ['clients', clientId],
    queryFn: () => getClient({ clientId })
  });
}

export function useClient({ clientId, queryConfig }: { clientId: number; queryConfig?: QueryConfig<typeof getClientQueryOptions> }) {
  return useQuery({
    ...getClientQueryOptions(clientId),
    ...queryConfig
  });
}

export function getClientsQueryOptions() {
  return queryOptions({
    queryKey: ['clients'],
    queryFn: () => getClients()
  });
}

export function useClients({ queryConfig }: { queryConfig?: QueryConfig<typeof getClientsQueryOptions> } = {}) {
  return useQuery({
    ...getClientsQueryOptions(),
    ...queryConfig
  });
}

export const useUpdateClient = ({ mutationConfig }: { mutationConfig?: MutationConfig<typeof updateClient> } = {}) => {
  const queryClient = useQueryClient();

  const { onSuccess, ...restConfig } = mutationConfig || {};

  return useMutation({
    onSuccess: async (...args) => {
      await queryClient.invalidateQueries({
        queryKey: getClientsQueryOptions().queryKey
      });
      await queryClient.invalidateQueries({ queryKey: getProjectsQueryOptions().queryKey });
      await queryClient.invalidateQueries({ queryKey: getContactsQueryOptions().queryKey });

      onSuccess?.(...args);
    },
    ...restConfig,
    mutationFn: updateClient
  });
};
